import { MapsAPILoader } from "@agm/core";
import { Component, ElementRef, NgZone, ViewChild } from "@angular/core";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { BehaviorSubject, combineLatest } from "rxjs";
import { debounce, debounceTime, map, switchMap, withLatestFrom } from "rxjs/operators";
import { MapsService } from "src/app/core/map/maps.service";
import { zoomForViewport } from "src/app/core/v2/map/viewport-to-zoom";
import { ApartmentMapFilter, ApartmentMapListItem, ApartmentService, MapBounds } from "src/app/core/v3/comparable/apartment/apartment.service";
import { emptyFilter, FilterChange, FilterModel } from "../../../shared_components/filter-section/filter-section.component";
import { madCalculator } from 'src/app/core/_base/crud/utils/map-marker.util';

interface State {
  items: ApartmentMapListItem[],
  zoom: number
  center?: {
    latitude: number,
    longitude: number
  },
  bounds?: {
    ne_lat: number,
    ne_lng: number,
    sw_lat: number,
    sw_lng: number
  },
  filter: FilterModel
}

@Component({
  selector: 'app-apartment-map',
  templateUrl: './apartment-map.component.html',
  styleUrls: ['./apartment-map.component.scss'],
  providers: [ComponentStore]
})
export class ApartmentMapComponent {
  @ViewChild('search', { static: false })
  public searchElementRef: ElementRef


  private _map: google.maps.Map = null
  // private _clusterer: MarkerClusterer = null;
  calculator = madCalculator
  icon = {
    url: './assets/media/icons/my_marker.svg',
  };

  // State
  private _filter$ = this._componentStore.select(state => state.filter)
  private _center$ = this._componentStore.select(state => state.center)
  private _centerLatitude$ = this._componentStore.select(this._center$, center => center?.latitude)
  private _centerLongitude$ = this._componentStore.select(this._center$, center => center?.longitude)
  private _items$ = this._componentStore.select(state => state.items)

  zoom$ = this._componentStore.select(state => state.zoom)
  filter$ = this._filter$
  centerLat$ = combineLatest([
    this._centerLatitude$,
    this._mapsService.latitude$
  ]).pipe(
    map(([lat, _default]) => {
      if (lat === null || lat === undefined) return _default
      return lat
    })
  )
  centerLng$ = combineLatest([
    this._centerLongitude$,
    this._mapsService.longitude$
  ]).pipe(
    map(([lng, _default]) => {
      if (lng === null || lng === undefined) return _default
      return lng
    })
  )
  items$ = this._componentStore.select(
    this._items$,
    (items) => {
      const map = new Map<string, {
        title: string,
        label: any,
        latitude: number,
        longitude: number,
        items: unknown[]
      }>()

      items.forEach(item => {
        const key = item.latitude + '_' + item.longitude
        if (map.has(key)) {
          const data = map.get(key)
          data.title = data.title + '/' + item.refNum
          data.label = this._getLabel(data.title)
          data.items.push(item)
        } else {
          map.set(key, {
            title: item.refNum,
            label: this._getLabel(item.refNum),
            latitude: item.latitude,
            longitude: item.longitude,
            items: [item]
          })
        }
      })

      return Array.from(map, ([key, value]) => ({ key, value }))
    }
  )

  constructor(
    private _mapsAPILoader: MapsAPILoader,
    private _ngZone: NgZone,
    private _componentStore: ComponentStore<State>,
    private _mapsService: MapsService,
    private _apartmentService: ApartmentService
  ) {
    this._componentStore.setState({
      items: [],
      zoom: 15,
      filter: emptyFilter()
    })
  }

  identity(index: number, item: { key: string }) {
    return item.key
  }

  onFilterChange(event: FilterChange) {
    this._loadItemsByFilter(event.filter)
  }

  onMapReady(event: google.maps.Map) {
    this._map = event
    this._mapsAPILoader.load().then(() => {
      if (this.searchElementRef) {
        const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
          types: ['geocode']
        })
        autocomplete.addListener('place_changed', () => {
          this._ngZone.run(() => {
            const place = autocomplete.getPlace()
            if (place.geometry === undefined || place.geometry === null) {
              return;
            }
            this._componentStore.patchState({
              center: {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng()
              },
              zoom: zoomForViewport(place.geometry.viewport)
            })
          })
        })
      }
    })
  }

  onBoundsChange(bounds: google.maps.LatLngBounds) {
    const _bounds = {
      ne_lat: bounds.getNorthEast().lat(),
      ne_lng: bounds.getNorthEast().lng(),
      sw_lat: bounds.getSouthWest().lat(),
      sw_lng: bounds.getSouthWest().lng()
    }
    this._loadItemsByBounds(_bounds)
  }

  private _loadItemsByBounds = this._componentStore.effect<MapBounds>(bounds$ =>
    bounds$.pipe(
      debounceTime(300),
      withLatestFrom(this._componentStore.state$),
      switchMap(([bounds, state]) =>
        this._apartmentService.fetchMap({
          bounds,
          filter: state.filter
        }).pipe(
          tapResponse(
            (response) => {
              this._componentStore.patchState({
                items: response.data,
                bounds
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _loadItemsByFilter = this._componentStore.effect<FilterModel>(filter$ =>
    filter$.pipe(
      withLatestFrom(this._componentStore.state$),
      switchMap(([filter, state]) =>
        this._apartmentService.fetchMap({
          filter,
          bounds: state.bounds
        }).pipe(
          tapResponse(
            (response) => {
              this._componentStore.patchState({
                filter
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _getLabel(title: string) {
    return {
      color: '#000000',
      fontFamily: '',
      fontSize: '14px',
      fontWeight: 'bold',
      className: 'bg-marker',
      text: title
    }
  }
}